<template>
    <div
        class="fav-list-item"
        role="button"
        tabindex="0"
        @click="updateFavoriteList"
        @keydown.enter="updateFavoriteList"
        @keydown.space.prevent="updateFavoriteList"
    >
        <div class="fav-list-item__details">
            <p class="fav-list-item__title">
                <VJoyIcon v-if="favoriteList.sharedWithinCompany" name="users" class="joy-mr-1" />
                <VJoyIcon v-else name="lock" class="joy-mr-1" />
                {{ favoriteListWithCount }}
            </p>
            <p class="fav-list-item__subtitle">
                <span>{{ $t('customer.freelancer.pof.my.favorites.creator') }}</span>
                &nbsp;
                <span>{{ owner }}</span>
            </p>
        </div>
        <div class="fav-list-item__avatars">
            <FreelancerAvatars :freelancers="favoriteList.firstProfiles" :total-count="favoriteList.count" />
        </div>
        <VJoyIcon :name="freelancerInList ? 'heart-fill' : 'heart'" size="medium" />
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {createSnackNotifier, useTranslation} from '#imports';
    import type {DeepReadonly} from '@malt/types-utils';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import type {FreelancersList} from '@api';
    import FreelancerAvatars from './FreelancerAvatars.vue';
    import {usePoolOfFreelancesStore} from '../../stores/pool-of-freelances.store';
    import type {FreelancerProfile} from '../types';

    const props = defineProps<{
        freelancer: FreelancerProfile;
        favoriteList: DeepReadonly<FreelancersList>;
        freelancerInList: boolean;
    }>();

    const {t} = useTranslation();
    const notifier = createSnackNotifier();
    const poolOfFreelancesStore = usePoolOfFreelancesStore();

    const owner = computed(() => {
        return props.favoriteList.ownerFullName ?? t('deleted.account.firstName');
    });

    const favoriteListWithCount = computed(() => {
        return `${props.favoriteList.name} (${props.favoriteList.count})`;
    });

    const summaryPageLink = computed(() => {
        return `/client/freelancers/list/${props.favoriteList.id}`;
    });

    function updateFavoriteList(): void {
        if (props.freelancerInList) {
            removeFavoriteFromList();
        } else {
            addFavoriteToList();
        }
    }

    async function addFavoriteToList() {
        try {
            await poolOfFreelancesStore.addToFavorite(props.freelancer, props.favoriteList.id);
            notifier.push(
                'success',
                t('customer.freelancer.pof.modal.add.favorite.success.v2', [
                    props.freelancer.fullName,
                    summaryPageLink.value,
                    props.favoriteList.name,
                ]),
            );
        } catch {
            notifier.push(
                'error',
                t('customer.freelancer.pof.modal.add.favorite.failed.v2', [
                    props.freelancer.fullName,
                    summaryPageLink.value,
                    props.favoriteList.name,
                ]),
            );
        }
    }

    async function removeFavoriteFromList() {
        try {
            await poolOfFreelancesStore.removeFromFavorite(props.freelancer, props.favoriteList.id);
            notifier.push(
                'success',
                t('customer.freelancer.pof.modal.remove.favorite.success.v2', [
                    props.freelancer.fullName,
                    summaryPageLink.value,
                    props.favoriteList.name,
                ]),
            );
        } catch {
            notifier.push(
                'error',
                t('customer.freelancer.pof.modal.remove.favorite.failed.v2', [
                    props.freelancer.fullName,
                    summaryPageLink.value,
                    props.favoriteList.name,
                ]),
            );
        }
    }
</script>

<style lang="scss" scoped>
    .fav-list-item {
        display: flex;
        justify-content: space-between;
        border-radius: var(--joy-core-radius-3);
        padding: var(--joy-core-spacing-5) var(--joy-core-spacing-5) var(--joy-core-spacing-5) var(--joy-core-spacing-9);
        align-items: center;
        border: 1px solid transparent;

        &:hover {
            cursor: pointer;
            border-color: var(--joy-color-secondary-30);
        }

        &__title {
            display: flex;
            align-items: center;
            font-size: var(--joy-font-size-primary-400);
            font-weight: var(--joy-font-weight-bold);
            margin-bottom: 0;
            text-overflow: ellipsis;
        }

        &__subtitle {
            margin-top: var(--joy-core-spacing-1);
            font-size: var(--joy-font-size-primary-300);

            > span:first-child {
                color: var(--joy-color-neutral-40);
            }

            > span:last-child {
                font-weight: var(--joy-font-weight-bold);
            }
        }

        &__avatars {
            min-width: 240px;
            margin-right: var(--joy-core-spacing-3);
        }
    }
</style>
